<template>
  <div v-if="isLoading" class="d-flex justify-content-center">
    <loader></loader>
  </div>

  <div v-else class="container spa-summary">
    <div class="page-intro">
      <h1>Congrats</h1>
      <h2>Your spa experience is booked!</h2>

      <p>
        We look forward to welcoming you to the Spa at Rockliffe Hall, and have
        sent a confirmation of your booking to the email address provided.
      </p>

      <span class="confirmation-number mb-4"
        >Confirmation Number: <strong>{{ confirmationNumber }}</strong></span
      >

      <div class="container mb-3 d-print-none">
        <div class="row justify-content-center">
          <div class="col-xl-12 text-center">
            <div class="row">
              <div class="col-md-12 ms-auto text-md-end mb-md-0 mb-2">
                <a class="lnk-print" v-on:click="printWindow()"
                  >Print Confirmation</a
                >
              </div>
              <div class="col-md-12 text-md-start">
                <a class="lnk-email" v-on:click="toggleSendToFriend()"
                  >Send to a friend</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="sendToFriend" class="row justify-content-center">
      <div class="col-xl-12 mb-5">
        <Form @submit="onSendEmail" :validation-schema="schema">
          <div class="p-5 bg-white has-shadow">
            <h3>Send to a friend</h3>
            <p>
              Enter an email address below and we'll send a copy of your
              confirmation.
            </p>

            <div v-if="!isEmailLoading">
              <div class="form-group row flex-row g-0">
                <div class="col-xl-16 mb-3 mb-xl-0">
                  <Field
                    name="emailAddress"
                    class="form-control"
                    placeholder="Email address"
                  ></Field>
                </div>
                <button class="btn btn--gold col-12 col-xl-8" type="submit">
                  Send
                </button>
              </div>
              <div class="form-group row flex-row g-0">
                <div class="col-xl-16 mb-3 mb-xl-0">
                  <ErrorMessage name="emailAddress" class="error-message" />
                </div>
              </div>
            </div>
            <div v-else>
              <div class="form-group row flex-row g-0">
                <div class="col-xl-16 mb-3 mb-xl-0">
                  <div class="d-flex justify-content-center">
                    <loader></loader>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>

    <div class="row mt-5 justify-content-center">
      <div class="col-xl-12">
        <div class="current-action">
          <span class="action">Payment successful</span>
        </div>

        <div class="bg-white gap-md-5 p-4 mb-md-5 mb-3">
          <h2>Paid Today:</h2>

          <div class="spa-summary-group mb-0">
            <div class="row bill">
              <div class="col-md-17 col-15">
                <div class="row mb-2">
                  <div class="col-md-7">Treatments</div>
                  <div class="col-md-7">
                    <strong> &pound;{{ treatmentsTotal() }}</strong>
                  </div>
                  <div class="col-10"></div>
                </div>
                <div class="row" v-if="hasAddOns">
                  <div class="col-md-7 col-9">Add Ons:</div>
                  <div class="col-md-17 col-15">
                    <strong> &pound;{{ addOnsTotal() }}</strong>
                  </div>
                </div>
              </div>
              <div class="col-md-7 col-9 bordered">
                <span class="spa-total"
                  >Total:
                  <span class="spa-total-amount"
                    >&pound;{{ total() }}</span
                  ></span
                >
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-24">
                <info-box v-bind:type="'success'">
                  <template v-slot:body>
                    <div class="d-flex justify-content-between">
                      <span class="heading"> Your payment was successful </span>
                    </div>
                    <div>
                      <p>Payment was taken from card {{ cardMasked }}</p>
                    </div>
                  </template>
                </info-box>
              </div>
            </div>
          </div>
        </div>

        <div class="current-action-wrapper">
          <div class="container">
            <div class="current-action">
              <span class="action">Your booking details</span>
            </div>
          </div>
        </div>

        <div
          v-if="createdAccount != null"
          class="row mb-4 d-flex justify-content-center"
        >
          <div class="col-lg-24">
            <div class="bg-white w-100 p-4 position-relative">
              <div v-if="createdAccount">
                <info-box v-bind:type="'success'">
                  <template v-slot:body>
                    <div class="d-flex justify-content-between">
                      <span class="heading">
                        Your Rockliffe Hall account was created successfully.
                      </span>

                      <a
                        v-if="!isAuthenticated"
                        href="/account/#/login"
                        class="account_login"
                      >
                        Login
                      </a>
                    </div>
                    <div>
                      <p>You can now login and review or amend your booking.</p>
                    </div>
                  </template>
                </info-box>
              </div>
              <div v-else>
                <info-box v-bind:type="'error'">
                  <template v-slot:body>
                    <span class="heading">
                      We were unable to create your Rockliffe Hall account.
                    </span>
                  </template>
                </info-box>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white p-md-5 p-4">
          <div class="spa-summary-group">
            <label> Date &amp; Guests </label>
            <div class="row mb-2">
              <div class="col-md-5 col-7">Date</div>
              <div class="col-md-19 col-17">
                {{ formatDate(search.date) }}
              </div>
            </div>

            <div class="row mb-2">
              <div class="col-md-5 col-7">Guests</div>
              <div class="col-md-19 col-17">
                {{ search.guestCount }}
              </div>
            </div>
          </div>

          <div
            class="spa-summary-group"
            v-for="(guest, index) in search.guests"
          >
            <label> {{ guest.firstName }}'s Treatments </label>
            <div class="row mb-3">
              <div class="col-24 col-lg-5">Treatments</div>
              <div class="col-24 col-lg-19">
                <ul class="treatments-list">
                  <li v-for="(t, index) in guest.treatments">
                    <div class="row">
                      <div class="col-18">
                        {{ t.treatment.DisplayName }}
                        {{ t.duration.Duration }}minutes
                      </div>
                      <div class="col-6">
                        <strong> &pound;{{ t.duration.Price }} </strong>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div v-if="hasAddOns">
            <hr />
            <h2>Add Ons</h2>

            <div
              class="spa-summary-group"
              v-for="(addOn, index) in search.groupAddOns"
            >
              <label>
                {{ addOn.content.DisplayName }}
              </label>
              <div class="row mb-3">
                <div class="col-24">
                  <div class="summary-add-on p-3 position-relative">
                    <button class="spa-edit mt-3" v-on:click="onEditAddOns()">
                      Edit
                    </button>

                    <div class="row">
                      <div class="col-24">
                        <div class="row">
                          <div class="col-20">
                            <label class="small">
                              {{ search.guests.length }}
                              {{
                                search.guests.length == 1 ? "pass" : "passes"
                              }}
                              :
                              {{ formatTime(addOn.selection.StartDateTime) }}
                              entry
                            </label>
                          </div>
                          <div class="col">
                            <strong class="mr-5"
                              >&pound;{{
                                search.guests.length *
                                addOn.selection.ActualPrice
                              }}</strong
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-24">
                        <div v-html="addOn.content.Description"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="spa-summary-group"
              v-if="search.selectedMudExtras != null"
            >
              <label>
                {{ search.selectedMudExtras.addOn.content.DisplayName }}
              </label>
              <div class="row mb-3">
                <div class="col-24">
                  <div class="summary-add-on p-3 position-relative">
                    <button class="spa-edit mt-3" v-on:click="onEditAddOns()">
                      Edit
                    </button>

                    <div class="row">
                      <div class="col-24">
                        <div class="row">
                          <div class="col-20">
                            <label class="small">
                              {{ search.selectedMudExtras.guestIndexes.length }}
                              passes :
                              {{
                                formatTime(
                                  search.selectedMudExtras.selection
                                    .StartDateTime
                                )
                              }}
                              entry
                            </label>
                          </div>
                          <div class="col">
                            <strong class="mr-5"
                              >&pound;{{
                                search.selectedMudExtras.selection.ActualPrice
                              }}</strong
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-24">
                        <div
                          v-html="
                            search.selectedMudExtras.addOn.content.Description
                          "
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <spa-itinerary
              v-bind:isSpaDay="false"
              v-bind:title="'Your Spa Treatment Itinerary'"
            ></spa-itinerary>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="d-print-none">
    <actions-bar>
      <template v-slot:actions>
        <div class="row">
          <div class="col-24 justify-content-center d-flex">
            <a href="/" class="btn btn--slate-blue"> Rockliffe Home </a>
            <a
              href="/account/#bookings/spa"
              v-if="isAuthenticated"
              class="btn btn--slate-blue"
              >Account
            </a>
          </div>
        </div>
      </template>
    </actions-bar>
  </div>
</template>

<script>
import { Field, Form, ErrorMessage } from "vee-validate";
import * as yup from "yup";

import ActionsBar from "@/shared/components/ActionsBar.vue";
import Loader from "@/shared/components/Loader.vue";
import InfoBox from "@/shared/components/InfoBox.vue";
import SpaItinerary from "../components/SpaItinerary.vue";

import { store } from "../store/index";
import { accountStore } from "@/account/store/index";

import spaapi from "@/shared/api/spa";

export default {
  name: "SpaTreatmentConfirmation",

  components: {
    Loader,
    ActionsBar,
    Form,
    Field,
    ErrorMessage,
    InfoBox,
    SpaItinerary,
  },
  computed: {
    isAuthenticated() {
      return accountStore.getters.isAuthenticated;
    },
    hasAddOns() {
      return (
        (this.search.groupAddOns != null && this.search.groupAddOns.length) ||
        this.search.selectedMudExtras != null
      );
    },
    session() {
      return store.getters.session;
    },
    search() {
      return store.getters.search;
    },
    booking() {
      return store.getters.booking;
    },
    createdAccount() {
      let result = null;
      let session = this.session;
      if (session.accountCreateStatus !== undefined) {
        if (session.accountCreateStatus.attempted) {
          if (session.accountCreateStatus.failed) {
            return false;
          }
          return true;
        }
      }

      return result;
    },
  },
  data() {
    const schema = yup.object().shape({
      emailAddress: yup.string().required().email().label("Email address"),
    });

    return {
      schema,
      sendToFriend: false,
      emailAddress: null,
      isEmailLoading: false,
      isLoading: false,
      confirmationNumber: null,
      cardMasked: null,
    };
  },

  mounted() {
    let o = this;

    o.isLoading = false;
    spaapi.getSystemBooking(o.session.Id, function (t) {
      o.confirmationNumber = t.ConfirmationId;
      o.cardMasked = t.Payment.CardMaskedNo;

      try {
        o.track();
      } catch (err) {
        console.log("Tracking error");
      }

      o.isLoading = false;
    });
  },

  methods: {
    track() {
      let items = [];
      let total = 0;

      if (this.search.selectedMudExtras?.selection != null) {
        items.push({
          item_id: this.search.selectedMudExtras?.code.Code,
          item_name: this.search.selectedMudExtras?.addOn.content.DisplayName,
          item_variant: this.search.selectedMudExtras?.code.Guests + " Guests",
          quantity: 1,
          price: +this.search.selectedMudExtras.selection.ActualPrice,
          item_category: "Spa Add Ons",
        });

        total += +this.search.selectedMudExtras.selection.ActualPrice;
      }

      for (var gi = 0; gi < this.search.guests.length; gi++) {
        let guest = this.search.guests[gi];
        let treatments = guest.treatments;
        for (let ti = 0; ti < treatments.length; ti++) {
          let treatment = treatments[ti];

          items.push({
            item_id: treatment.duration.Code,
            item_name: treatment.treatment.DisplayName,
            quantity: 1,
            price: +treatment.duration.Price,
            item_category: "Treatments",
          });

          total += +treatment.duration.Price;
        }
      }

      if (this.$gtm.enabled()) {
        window.dataLayer?.push({
          pageURL: "/spa/treatment/confirmation",
          event: "purchase",
          transaction_id: this.confirmationNumber,
          value: total,
          curency: "GBP",
          ecommerce: {
            items: items,
          },
        });
      }

      var purchase = {
        transaction_id: this.confirmationNumber,
        affiliation: "Rockliffe Hall Spa Booking",
        value: total,
        currency: "GBP",
        items: items,
      };

      this.$gtag.purchase(purchase);
    },
    session() {
      return store.getters.session;
    },
    onSendEmail(values) {
      let o = this;
      this.isEmailLoading = true;

      let payload = {
        confirmation: o.session.Id,
        emailAddress: values.emailAddress,
      };

      let so = this;
      spaapi.sendEmail(
        payload,
        (t) => {
          this.isEmailLoading = false;
        },
        (f) => {
          this.isEmailLoading = false;
        }
      );
    },

    printWindow: function () {
      window.print();
    },
    toggleSendToFriend: function () {
      this.sendToFriend = !this.sendToFriend;
    },

    formatDate(input) {
      let date = moment(input);
      return date.format("ddd Do MMMM YYYY");
    },
    addOnsTotal() {
      return store.getters.addOnsTotal;
    },
    treatmentsTotal() {
      return store.getters.treatmentsTotal;
    },
    total() {
      return store.getters.total;
    },
    formatTime(dateTime) {
      let date = moment(dateTime);
      return date.format("HH:mm");
    },
  },
};
</script>

<style>
.spa-summary .page-intro h1 {
  font-size: 180px !important;
  font-family: "heatsregular";
  text-transform: none;
}

.treatments-list,
.treatments-list li {
  list-style: none;
  padding: 0;
  font-size: 16px;
}

.booking .spa-summary h1,
.booking .spa-summary h2 {
  font-size: 24px;
  margin-bottom: 30px;
  text-transform: none;
}

.spa-summary-group {
  margin-bottom: 40px;
  position: relative;
}

.spa-summary-group label {
  font-weight: bold;
  font-size: 14px !important;
  margin-bottom: 10px;
}

.spa-edit {
  border: none;
  background-color: transparent;
  position: absolute;
  right: 0%;
  top: 0%;
  height: 15px;
  text-indent: -9999px;
  width: 15px;
  background-position: center center;
  background-size: 15px 15px;
  background-repeat: no-repeat;
  background-image: url("@/assets/img/booking/edit.svg");
}

label.small {
  font-size: 12px;
}

.summary-add-on {
  border: solid 1px #aaa;
}

.summary-add-on .spa-edit {
  right: 15px;
}

.summary-add-on p {
  width: 85%;
}

.bordered {
  border-left: solid 1px #aaa;
}

.bill {
  min-height: 75px;
}

.spa-total {
  font-size: 24px;
}

.spa-total-amount {
  font-weight: bold;
}

.current-action-wrapper {
  text-align: center;
}

.current-action {
  width: 100%;
  font-family: "Playfair Display";
  font-size: 24px;
  position: relative;
  text-align: center;
  margin-bottom: 25px;
}

.current-action:after {
  width: 100%;
  content: "";
  height: 1px;
  background: #000;
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  z-index: 1;
  opacity: 0.1;
  display: none;
}

.current-action .action {
  padding: 0;
  background: #eae5e3;
  position: relative;
  z-index: 2;
}

.current-action .action .room-index {
  font-weight: bold;
}

.current-action .summary {
  margin-top: 30px;
}

.current-action-wrapper p.summary {
  font-size: 18px;
}

.spa-summary .lnk-print {
  margin-right: unset;
}

@media (min-width: 1025px) {
  .current-action {
    font-size: 36px;
  }

  .current-action:after {
    display: block;
  }
}

@media (min-width: 1200px) {
  .current-action .action {
    padding: 0 45px;
  }
}

@media (max-width: 767px) {
  .spa-summary-group {
    margin-bottom: 25px;
  }

  .spa-summary .page-intro h1 {
    font-size: 150px !important;
  }

  .booking .spa-summary h1,
  .booking .spa-summary h2 {
    margin-bottom: 20px;
  }
}

@media (max-width: 575px) {
  .spa-summary .page-intro h1 {
    font-size: 130px !important;
  }
}
</style>
